import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Explicit } from "../../components/ui/Explicit";
import { DrawerLink } from "../../app/DrawerLink";
import { Link } from "react-router-dom";

import { Button, LinkButton } from "../../components/ui/Button";
import { FavoriteButton } from "../../components/ui/FavoriteButton";
import { AddToPlaylist } from "../library/AddToPlaylist";
import { useTrackFavorite } from "../tracks/useTrackFavorite";
import { DiscoveryPulse } from "../../components/ui/Loader";
import { useSession } from "../../stores/session";
import { Artwork } from "../dashboard/Artwork";
import { ChatPanel } from "./ChatPanel";
import { SimulcastLink } from "./Simulcast";
import { WatchElsewhere } from "./WatchElsewhere";
import { BoostButton } from "../payments/BoostButton";
import { TipButton } from "../payments/TipButton";
import { useHostingHere } from "./hosting";
import { StreamPlayer } from "./StreamPlayer";
import { SubmitDialog } from "./SubmitDialog";
import { positionNow } from "./sync";
import { useRoomTuning } from "../../stores/room";
import { Crowd } from "./Crowd";
import { startListening, stopListening } from "./listening";
import { TwitchChat, TwitchPlayer, twitchChannel } from "./TwitchStage";
import { useRoom } from "./useRoom";
import { ambientCss, type BannerGradient } from "../profile/banner";
import {
  duration,
  elapsed,
  startsIn,
  TIER_TONE,
  type Room,
  type RoomView,
} from "./api";

/**
 * The live banner on a host's profile
 * (dev/design/GetVibed Profile Live.dc.html).
 *
 * ## A broadcast is not a place, it is a person doing something
 *
 * There is no room URL. When somebody is on air, their profile grows this
 * section above everything else and you scroll past it to the usual page. That
 * is the whole model: the audience is their followers, the address is their
 * profile, and "whose room is this" has exactly one answer.
 *
 * ## Renders nothing when nobody is live
 *
 * Which is almost always, for almost everybody. `useRoom` is asked for every
 * profile and returns `room: null` for anyone who has never hosted - not a 404,
 * because that is the ordinary case and treating it as an error would mean
 * every profile view logged one.
 */
export function LiveSection({
  username,
  displayName,
  banner,
  gradient,
  canBeTipped = false,
}: {
  username: string;
  displayName: string;
  /** The host's header image, if they uploaded one. */
  banner: string | null;
  /** And the gradient under it, which every profile has. */
  gradient: BannerGradient;
  /** Whether a tip would actually reach them. See `Profile.can_be_tipped`. */
  canBeTipped?: boolean;
}) {
  /*
   * Whether you are listening is a fact about the session, not about this
   * component. It used to be local state, which meant navigating away and back
   * showed "Listen live" to somebody who was already hearing the broadcast -
   * and clicking it re-tuned a room they had never left.
   *
   * `listen: false` because the heartbeat belongs to `RoomFollower` now: being
   * in the room follows the listener, not the route.
   */
  const tuned = useRoomTuning((state) => state.tuned);
  const { data } = useRoom(username, { listen: false });

  const room = data?.room ?? null;

  // Off air is the same as never having hosted, as far as this section goes.
  if (!room || room.status !== "live") return null;

  return (
    <Live
      room={room}
      view={data!}
      banner={banner}
      gradient={gradient}
      listening={tuned?.username === username}
      onListen={() => startListening(room)}
      displayName={displayName}
      canBeTipped={canBeTipped}
    />
  );
}

function Live({
  room,
  view,
  banner,
  gradient,
  listening,
  onListen,
  displayName,
  canBeTipped,
}: {
  room: Room;
  view: RoomView;
  banner: string | null;
  gradient: BannerGradient;
  listening: boolean;
  onListen: () => void;
  displayName: string;
  canBeTipped: boolean;
}) {
  const user = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  /*
   * The Twitch handle, when there is one to frame.
   *
   * Null for every other platform and for a link this cannot read, both of
   * which fall back to pointing at the door rather than framing a guess - see
   * `TwitchStage`.
   */
  const twitch = twitchChannel(room);

  return (
    /*
     * Full-bleed and at least a screen tall, per the mockup.
     *
     * Deliberately outside the site's `max-w-[1500px]` column, unlike every
     * other page: a broadcast in progress is the reason anybody is on this
     * profile, and boxing it into the same measure as a track listing makes it
     * read as one more section rather than as something happening now.
     *
     * `100dvh` minus the header *and the dock*, so the whole thing is visible
     * without scrolling and the profile begins exactly at the fold - which is
     * what makes "scroll for profile" a true instruction rather than a hint
     * about something already half on screen.
     *
     * The dock matters: it is fixed over the bottom of every page, so a section
     * sized to the header alone has its last row - the queue strip, the one
     * thing a listener checks for their own submission - hidden underneath it.
     *
     * `dvh` rather than `vh` because mobile browsers shrink the viewport when
     * their chrome appears, and `vh` would leave this taller than the screen
     * for the entire visit.
     */
    <section
      data-live
      className="flex min-h-[calc(100dvh-var(--spacing-chrome-top)-var(--spacing-dock))] flex-col items-stretch border-b border-line bg-void lg:flex-row"
    >
      {/*
        Three rows: the status line, the track, and what is coming up. The
        middle one grows and centres its contents, so the track sits in the
        middle of the screen however tall it is - rather than everything
        bunching at the top with a field of gradient underneath.
      */}
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        <Ambient banner={banner} gradient={gradient} />

        {/* Above the wash. Absolutely-positioned siblings paint over static
            ones, so this has to be positioned to win rather than merely
            declared later. */}
        {/*
          `data-live-stage` switches this subtree to the dark palette whatever
          the theme is - see the block at the end of `index.css`. The wash under
          it is near-black in both themes because it lies over the host's
          banner, so the text on it has to be the dark theme's text or it
          vanishes in a light one.
        */}
        <div
          data-live-stage
          className="relative z-10 flex flex-1 flex-col gap-7 px-6 py-8 lg:px-9"
        >
          <Status room={room} displayName={displayName} canBeTipped={canBeTipped} />

          {/*
            The stream where the deck would be.

            A host simulcasting to Twitch is putting the show out there, so that
            is what the room shows. The queue below stays: people still submit
            and boost from here, and that is the half of the room Twitch cannot
            do. See `TwitchStage`.
          */}
          {twitch ? (
            <div className="flex flex-1 items-center justify-center">
              <TwitchPlayer channel={twitch} room={room} />
            </div>
          ) : (
            <NowPlaying
              room={room}
              listening={listening}
              onListen={onListen}
              user={user}
              onSubmit={() => setSubmitting(true)}
            />
          )}
          {/*
            Who is actually in the room.

            Beside the deck rather than inside it: `NowPlaying` returns early
            when nothing is playing, and the audience does not leave between
            tracks. That gap is when a host most wants to see the room is still
            full - they are deciding what to play next, and an empty screen
            saying "between tracks" tells them nothing about whether anybody is
            still waiting.

            Under the deck and above the queue, because it is the room rather
            than a statistic - the count stays in the status line for anybody
            who only wants the number.
          */}
          {/*
            Standing on the line between the room and its queue.

            The negative margin cancels the column's gap so the crowd sits
            directly on the queue's top border, and the nudge down puts their
            contact shadows on it - so they read as people standing at the front
            of the room rather than as a strip of artwork floating above it.
            `z-10` because the figures overlap that border and would otherwise
            be drawn under it.
          */}
          {/*
            No crowd over a stream. The audience for a simulcast is on Twitch
            and counted by Twitch; a row of three faces under a stream with
            four hundred viewers in it would be a smaller number pretending to
            be the room.
          */}
          {!twitch && (
            <div className="relative z-10 -mb-7 translate-y-[9px]">
              <Crowd username={room.username} stationId={room.id} listening={listening} />
            </div>
          )}

          <UpNext room={room} view={view} />
        </div>
      </div>

      {/*
        Its own column at desk width, stacked underneath below it. Slightly
        darker than the gradient so it reads as a panel rather than as more of
        the same surface, which is what the mockup does.
      */}
      {/*
        Chat, or where the chat actually is.

        A host simulcasting to Twitch or TikTok is reading that chat, not this
        one - so showing an empty box beside a busy stream would split the
        audience and make a full room look deserted. See `WatchElsewhere`.

        No horizontal padding: `ChatPanel` pads its own rows so the message
        list's scrollbar reaches the edge of the column.
      */}
      <aside
        className={`flex min-h-[22rem] min-w-0 flex-col border-t border-line bg-well lg:w-[380px] lg:min-h-0 lg:border-l lg:border-t-0 ${
          /* Twitch's chat brings its own frame and its own dark ground, so our
             padding would show as a pale gutter around it - and does, in a
             light theme. Everything else wants the breathing room. */
          twitch ? "" : "py-6"
        }`}
      >
        {twitch ? (
          <TwitchChat channel={twitch} />
        ) : room.simulcast ? (
          <WatchElsewhere room={room} />
        ) : (
          <ChatPanel
            username={room.username}
            stationId={room.id}
            divided={false}
            canChat={view.can_chat}
            policy={room.chat}
          />
        )}
      </aside>

      {submitting && user && (
        <SubmitDialog
          room={room}
          username={user.username}
          onClose={() => setSubmitting(false)}
          onSubmitted={() => {
            setSubmitting(false);
            void queryClient.invalidateQueries({
              queryKey: ["room", room.username],
            });
          }}
        />
      )}
    </section>
  );
}

/**
 * The room, wearing the host's colours.
 *
 * ## An uploaded header becomes the room's light
 *
 * Blurred hard and dropped to a third opacity, which is the ambient-backdrop
 * trick every video player uses - and it works here for the same reason: a
 * photograph at 8% of its detail is a colour field, and a colour field is
 * exactly what a dark room wants behind it. The wash on top is what keeps the
 * status line and the track title readable over whatever somebody uploaded,
 * which is the part that cannot be left to taste.
 *
 * ## Otherwise the gradient they chose
 *
 * Muted into the mockup's near-black radial rather than replacing it - see
 * `ambientCss`. No image, no blur, no second request: the colours arrived with
 * the profile.
 *
 * `aria-hidden` and `pointer-events-none` throughout. This is light, not
 * content.
 */
function Ambient({
  banner,
  gradient,
}: {
  banner: string | null;
  gradient: BannerGradient;
}) {
  if (banner === null) {
    return (
      <div
        aria-hidden
        data-live-ambient="gradient"
        className="pointer-events-none absolute inset-0"
        style={{ background: ambientCss(gradient) }}
      />
    );
  }

  return (
    <div
      aria-hidden
      data-live-ambient="image"
      className="pointer-events-none absolute inset-0"
    >
      {/*
        Scaled past the edges, because `blur` samples nothing outside the
        element and would otherwise fade the border to transparent.
      */}
      {/*
        Dimmed and saturated rather than just faded.
        
        Fading alone loses the colour along with the brightness, which left a
        strong header reading as a grey smudge - the one thing this is for. So
        `brightness` clamps how light it can get, which is what keeps white text
        readable over a bright upload, and `saturate` puts back the hue that
        clamping costs.
      */}
      <img
        src={banner}
        alt=""
        className="h-full w-full scale-125 object-cover opacity-70 blur-3xl brightness-[0.55] saturate-[1.7]"
      />

      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 85% at 18% 0%, var(--color-veil-soft) 0%," +
            " var(--color-veil) 46%, var(--color-veil-strong) 100%)",
        }}
      />
    </div>
  );
}

/** The on-air light, the count, and what the set is called. */
function Status({
  room,
  displayName,
  canBeTipped,
}: {
  room: Room;
  displayName: string;
  canBeTipped: boolean;
}) {
  // Ticks so the elapsed clock counts. One second is the resolution shown.
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {/*
        The set's name, on its own line and at a size worth reading.
 
        It used to be the third item in the metadata row, in eleven-pixel mono
        next to the listener count - so a host who had bothered to name their
        broadcast got it rendered smaller than the word "listening". A name is
        the one piece of that row somebody chose; the rest is measurements.
 
        Still conditional. Most sets are just somebody going live, and an
        invented title would be worse than none - which is also why the fallback
        lives in the console header rather than here: a room called after its
        host does not need to say so twice on the host's own page.
      */}
      {room.name && (
        <h2
          data-room-name-display
          className="max-w-[22ch] text-2xl font-bold leading-tight tracking-tight text-ink sm:max-w-none"
        >
          {room.name}
        </h2>
      )}

      <div className="flex flex-wrap items-center gap-3.5">
          <span className="flex items-center gap-2.5 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-4 py-2">
          <span className="h-2 w-2 rounded-full bg-hot" aria-hidden />
          <span className="meta-caps text-hot-text">
            Live · {elapsed(room.live_started_at)}
          </span>
        </span>

        <span className="font-mono text-[11px] text-soft">
          {room.listener_count} listening · peak {room.peak_listeners}
        </span>

        {/*
          Here rather than beside the listener's other actions.
 
          Those live inside the now-playing block, which has three branches - a
          track, a video, and between tracks - so a button placed there vanishes
          every time a set changes over. Tipping is about the host and the room,
          not about whatever is currently spinning, so it belongs in the row that
          is always on screen while they are live.
        */}
        {canBeTipped && (
            <span className="ml-auto">
              <TipButton username={room.username} displayName={displayName} />
            </span>
          )}
      </div>
    </div>
  );
}

function NowPlaying({
  room,
  listening,
  onListen,
  user,
  onSubmit,
}: {
  room: Room;
  listening: boolean;
  onListen: () => void;
  user: { username: string } | null;
  onSubmit: () => void;
}) {
  const [position, setPosition] = useState(0);
  const watch = useRoomTuning((state) => state.watch);

  /*
   * The clock runs locally between server updates.
   *
   * Asking the server four times a second for a number it already told us how
   * to compute would be absurd; `positionNow` extrapolates from the last known
   * position and the corrected clock, and every poll or pushed event snaps it
   * back to the truth. Clamped to the track's length - the server rolls a
   * finished room over on the next read, but a bar reading past the end is
   * wrong for the second before it does.
   */
  useEffect(() => {
    const update = () => {
      const at = positionNow(room) ?? 0;
      const length = room.now_playing?.duration_ms;

      setPosition(length == null ? at : Math.min(at, length));
    };

    update();
    const timer = setInterval(update, 400);

    return () => clearInterval(timer);
  }, [room]);

  /*
   * A held room reads as between tracks, not as a paused one.
   *
   * With auto-play off the finished track stays loaded so the host can review
   * it, so `now_playing` is still set - but from the room's side it is over.
   * Showing it with a full progress bar and a paused deck would tell listeners
   * the broadcast had stalled, which is the opposite of what is happening.
   */
  const track = room.holding ? null : room.now_playing;

  /*
   * Fills in the heart. The room's own payload cannot say whether *you* have
   * favourited what is playing - it goes to every listener at once - so this
   * asks separately and puts the answer where the button reads it.
   */
  useTrackFavorite(track?.id);

  /*
   * Whether this tab is the one sending the broadcast.
   *
   * The studio lives in the app shell now, so a host can be publishing and
   * looking at their own profile at the same time - in which case the stream
   * must not be played back here, or they hear the deck they are mixing from
   * *and* its own echo three seconds later.
   */
  const producing = useHostingHere(room.username);

  /*
   * While a stream is on screen, the dock gets out of the way.
   *
   * It is a transport for audio the dock is playing, and under a broadcast it
   * was playing none - it read "Nothing playing / pick a track to start" over
   * the top of something somebody was watching. `PlayerBar` still shows itself
   * if a track *is* loaded, so playing one of the artist's records from further
   * down this page keeps its controls.
   *
   * Cleared on the way out, or the next page inherits a hidden dock from a
   * broadcast nobody is watching any more.
   */
  const streaming = room.stream_url !== null && !producing;

  useEffect(() => {
    watch(streaming ? room.username : null);

    return () => watch(null);
  }, [streaming, room.username, watch]);

  /*
   * Between tracks.
   *
   * Not a placeholder for a missing thing - it is a real moment in a broadcast,
   * and the most useful one this page has: the queue is empty, the host is
   * waiting, and whoever is reading this can fix that in two clicks. A line of
   * grey text in a screen of gradient wasted it.
   */
  /*
   * A studio broadcast, which has a picture.
   *
   * Rendered before the between-tracks branch below, because in studio mode the
   * gap between records is exactly when the host is talking - "nothing playing"
   * is the least accurate thing this page could say while somebody is on camera
   * mid-sentence.
   *
   * ## The picture is the page
   *
   * This used to put the video on the left and a full now-playing block beside
   * it - the same title and artist the host has already composited *into* the
   * frame, at four times the size, competing with the thing it was describing.
   * The video takes the width now, and what is playing is a line underneath it,
   * which is also where it belongs when the host has the overlay switched off
   * and this is the only place it appears.
   */
  if (room.stream_url) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-5">
        <div className="w-full max-w-[min(1100px,calc(100vh-19rem)*1.777)]">
          {producing ? (
            /*
              Not the stream, when this tab is the one sending it.

              A host who navigates to their own profile mid-broadcast would
              otherwise be handed their own picture three seconds late, with the
              audio doubled against the deck they are mixing from.
            */
            <div className="flex aspect-video w-full items-center justify-center rounded-[20px] border border-frame bg-black/60 px-8 text-center">
              <p className="text-sm text-soft">
                You are broadcasting this from the studio. Your listeners see it
                here.
              </p>
            </div>
          ) : (
            <StreamPlayer
              url={room.stream_url}
              hls={room.stream_hls}
              listening={listening}
              onJoin={onListen}
              onLeave={() => stopListening()}
            />
          )}
        </div>

        <div className="flex w-full max-w-[min(1100px,calc(100vh-19rem)*1.777)] flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="meta-caps shrink-0 text-faint">
              {track ? "Playing" : "On air"}
            </span>

            {track ? (
              <span className="min-w-0 truncate text-[15px] text-ink">
                <DrawerLink
                  to={`/tracks/${track.id}`}
                  className="hover:underline"
                >
                  {track.explicit && (
                    <Explicit className="mr-1.5 align-[-2px]" />
                  )}
                  {track.title}
                </DrawerLink>
                <span className="text-text-2">
                  {" · "}
                  <DrawerLink
                    to={`/${track.artist.username}`}
                    className="hover:text-ink hover:underline"
                  >
                    {track.artist.display_name}
                  </DrawerLink>
                </span>
              </span>
            ) : (
              <span className="truncate text-[15px] text-text-2">
                {room.host.display_name}
              </span>
            )}
          </div>

          {/*
            No "Join the room" here any more.

            It sat beside the video asking for a decision the play button in the
            middle of the picture already asks for, in the place people look for
            it. Two controls for one intention, and the one further from the
            video was the one people found first.
          */}
          <div className="flex shrink-0 flex-wrap items-center gap-2.5">
            <SimulcastLink simulcast={room.simulcast} />
            <SubmitButton room={room} user={user} onSubmit={onSubmit} />
          </div>
        </div>
      </div>
    );
  }

  if (!track) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 py-10 text-center">
        {/*
          The equaliser, not the broadcast loader. Red is what the status line
          uses for "on air" and repeating it here would read as an alarm; green
          bars idling on a baseline read as what this is - the air is open and
          nothing is on it. Scaled up because the space is a screen tall and a
          32px animation floating in the middle of it looks like a mistake.
        */}
        <DiscoveryPulse className="scale-[1.6] opacity-70" />

        <div className="flex max-w-md flex-col gap-2">
          <h2 className="text-2xl font-semibold tracking-tight text-ink">
            {room.host.display_name} is between tracks.
          </h2>
          <p className="text-soft">
            {room.holding
              ? "That one just finished. The next is up to them."
              : room.submissions === "closed"
                ? "Still on air — the next one is coming."
                : "The queue is empty. Whatever goes in next plays next."}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2.5">
          <SimulcastLink simulcast={room.simulcast} />
          {!listening && (
            <Button variant="secondary" onClick={onListen} data-tune-in>
              Listen live
            </Button>
          )}
          <SubmitButton room={room} user={user} onSubmit={onSubmit} />
        </div>
      </div>
    );
  }

  const remaining =
    track.duration_ms == null
      ? null
      : Math.max(0, track.duration_ms - position);
  const progress = track.duration_ms
    ? Math.min(100, (position / track.duration_ms) * 100)
    : 0;

  return (
    // `flex-1` so this is the row that absorbs the section's spare height, and
    // `items-center` so the track sits in the middle of it.
    <div className="flex flex-1 flex-wrap items-center gap-9">
      <Artwork
        track={{
          title: track.title,
          artwork: track.artwork,
          artwork_small: track.artwork_small,
        }}
        large
        className="aspect-square w-full max-w-[min(340px,32vh)] shrink-0 rounded-[20px] border border-frame"
      />

      <div className="flex min-w-[16rem] flex-1 flex-col gap-5">
        <div className="flex flex-col gap-2.5">
          <span className="meta-caps text-faint">Now playing</span>

          <h2 className="text-4xl font-bold leading-[1.08] tracking-[-0.03em] text-ink xl:text-[38px]">
            <DrawerLink to={`/tracks/${track.id}`} className="hover:underline">
              {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
              {track.title}
            </DrawerLink>
          </h2>

          <Link
            to={`/${track.artist.username}`}
            className="text-[17px] text-text-2 hover:text-ink hover:underline"
          >
            {track.artist.display_name}
          </Link>
        </div>

        {/*
          Read-only, and deliberately not a scrub bar. Everybody here is at the
          same second - that is what a broadcast is - and `useRoomSync` would
          undo a seek anyway. A control that undoes itself is worse than none.
        */}
        <div className="flex flex-col gap-2">
          <div className="h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-track">
            <div
              className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between font-mono text-[11px] text-soft">
            <span data-live-position>{duration(position)}</span>
            <span>{remaining === null ? "—" : `-${duration(remaining)}`}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {listening ? (
            <Button variant="secondary" disabled data-listening>
              Tuned in
            </Button>
          ) : (
            <Button onClick={onListen} data-tune-in>
              Listen live
            </Button>
          )}

          {/*
              No `favorited` prop: the room payload cannot carry one, because it
              is broadcast to every listener at once. `useTrackFavorite` above
              puts the answer in the store this button already reads.
          */}
          <FavoriteButton
            trackId={track.id}
            size="button"
            className="rounded-[var(--radius-pill)] border border-line px-4 py-2.5"
          />

          {/*
            Hearing something in a room is one of the main ways anybody finds
            music here, and until now the only way to keep it was the heart -
            which files everything into one undifferentiated pile.
          */}
          <AddToPlaylist trackId={track.id} title={track.title} size="sm" />

          {/* Beside listening rather than buried in the status line: somebody
              who wants to watch as well as hear is making that choice here. */}
          <SimulcastLink simulcast={room.simulcast} />

          <SubmitButton room={room} user={user} onSubmit={onSubmit} />

        </div>

      </div>
    </div>
  );
}

/**
 * The queue, as a strip.
 *
 * Horizontal rather than a list, because on a profile this is a glance - "is
 * mine coming up" - and a vertical queue would push the actual profile off the
 * screen. The console is where a queue gets worked on.
 */
function UpNext({ room, view }: { room: Room; view: RoomView }) {
  const me = useSession((state) => state.user);

  /*
   * The host has turned the contents off.
   *
   * Still a row rather than nothing, and still carrying the count - somebody
   * deciding whether to submit needs to know there is a queue and roughly how
   * long it is, which is the question the strip was answering anyway. What
   * they no longer get is the running order.
   */
  if (!room.show_queue) {
    return (
      <div className="flex flex-wrap items-center gap-5 border-t border-line pt-5">
        <span className="meta-caps shrink-0 text-faint">Up next</span>
        <span
          className="min-w-0 flex-1 font-mono text-[11px] text-soft"
          data-up-next-hidden
        >
          {view.session.queued === 0
            ? "Nothing queued yet."
            : `${view.session.queued} waiting — ${room.host.display_name} is keeping the running order to themselves.`}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-ghost">
          scroll for profile ↓
        </span>
      </div>
    );
  }

  if (view.queue.length === 0) {
    return (
      <div className="flex items-center gap-5 border-t border-line pt-5">
        <span className="meta-caps shrink-0 text-faint">Up next</span>
        <span className="font-mono text-[11px] text-soft">
          Nothing queued yet.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-5 border-t border-line pt-5">
      <span className="meta-caps shrink-0 text-faint">Up next</span>

      <ul
        className="flex min-w-0 flex-1 gap-2.5 overflow-x-auto pb-1"
        data-up-next
      >
        {view.queue.slice(0, 8).map((item) => (
          <li
            key={item.id}
            className="flex shrink-0 items-center gap-3 rounded-[var(--radius-card)] border border-line bg-surface p-2.5 pr-3.5"
          >
            <Artwork
              track={{
                title: item.track.title,
                artwork: null,
                artwork_small: item.track.artwork_small,
              }}
              className="h-9 w-9 shrink-0 rounded-[var(--radius-chip)]"
            />
            <div className="flex flex-col gap-0.5">
              <span className="whitespace-nowrap text-[13px] font-bold text-ink">
                {item.track.explicit && (
                  <Explicit className="mr-1 align-[-2px]" />
                )}
                {item.track.title}
              </span>
              {/* The tier and the turn, which is what somebody scanning this
                  strip actually wants: is mine soon, and did paying matter. */}
              <span
                className={`whitespace-nowrap font-mono text-[9px] ${TIER_TONE[item.boost_tier]}`}
              >
                {item.boost_tier === "pick" ? "host pick" : item.boost_tier} ·{" "}
                {startsIn(item.starts_in_ms)}
              </span>
            </div>

            {/*
              Your own row, and only yours.

              The strip is where somebody watches for their track coming up, so
              it is where they decide it is not coming up fast enough. The
              server refuses a boost on anybody else's submission - see
              `BoostCheckoutAction` - and drawing the button anyway would be
              offering something that 403s.
            */}
            {room.boosts && item.submitter?.username === me?.username && (
              <BoostButton
                username={room.username}
                queueId={item.id}
                tier={item.boost_tier}
                prices={room.boosts}
              />
            )}
          </li>
        ))}
      </ul>

      <span className="shrink-0 font-mono text-[10px] text-ghost">
        scroll for profile ↓
      </span>
    </div>
  );
}

/**
 * The one control that opens the submit dialog.
 *
 * Shared by the playing state and the between-tracks state, so the two cannot
 * disagree about when submitting is possible - which they did: the button used
 * to live inside the now-playing block and vanished the moment a broadcast ran
 * out of queue, at exactly the point a host most wants something to play.
 *
 * Only the host's policy hides it. Whether a track is playing has nothing to do
 * with whether the queue is open, and an empty queue is the moment a submission
 * is worth the most.
 */
function SubmitButton({
  room,
  user,
  onSubmit,
}: {
  room: Room;
  user: { username: string } | null;
  onSubmit: () => void;
}) {
  if (room.submissions === "closed") {
    return (
      <span className="font-mono text-[11px] text-faint">
        Submissions closed
      </span>
    );
  }

  if (!user) {
    return (
      <LinkButton to="/register" variant="secondary">
        Join to submit
      </LinkButton>
    );
  }

  return (
    <Button variant="secondary" onClick={onSubmit} data-open-submit>
      Submit a track
    </Button>
  );
}
